// Base API configuration and utilities
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

export interface ApiResponse<T> {
  data: T | null;
  message?: string;
  error?: string;
  success: boolean;
}

export class ApiError extends Error {
  public status: number;
  public code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

// Request interceptor for adding auth headers
const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

// Response interceptor for handling errors
const handleResponse = async <T>(response: Response): Promise<T> => {
  // #region agent log
  console.log('[DEBUG] handleResponse:', { status: response.status, ok: response.ok, url: response.url });
  // #endregion
  
  // For 404 errors, try to parse JSON but return a structured response instead of throwing
  if (response.status === 404) {
    try {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || errorData.error || 'Resource not found';
      // Return a structured error response for 404 instead of throwing
      // Use 'unknown' first to satisfy TypeScript's type checking
      return {
        success: false,
        message: errorMessage,
        error: errorMessage,
        data: null as any
      } as unknown as T;
    } catch (parseError) {
      return {
        success: false,
        message: 'Resource not found',
        error: 'Resource not found',
        data: null as any
      } as unknown as T;
    }
  }
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // Handle different error response formats
    const errorMessage = errorData.message || errorData.error || `HTTP error! status: ${response.status}`;
    // #region agent log
    console.log('[DEBUG] handleResponse error:', { status: response.status, errorMessage, errorData });
    // #endregion
    throw new ApiError(
      errorMessage,
      response.status,
      errorData.code
    );
  }
  const jsonData = await response.json();
  // #region agent log
  console.log('[DEBUG] handleResponse success:', { success: jsonData.success, message: jsonData.message });
  // #endregion
  return jsonData;
};

// Base API service class
export class ApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  protected async get<T>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    if (params) {
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          url.searchParams.append(key, params[key].toString());
        }
      });
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    return handleResponse<ApiResponse<T>>(response);
  }

  protected async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });

    return handleResponse<ApiResponse<T>>(response);
  }

  protected async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });

    return handleResponse<ApiResponse<T>>(response);
  }

  protected async patch<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });

    return handleResponse<ApiResponse<T>>(response);
  }

  protected async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    // #region agent log
    const token = localStorage.getItem('authToken');
    fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.ts:delete',message:'Delete request',data:{endpoint,url:`${this.baseUrl}${endpoint}`,hasToken:!!token},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A,E'})}).catch(()=>{});
    // #endregion
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    
    // #region agent log
    console.log('[DEBUG] Delete response:', { status: response.status, statusText: response.statusText, ok: response.ok });
    fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.ts:delete-response',message:'Delete response',data:{status:response.status,statusText:response.statusText,ok:response.ok},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D,F'})}).catch(()=>{});
    // #endregion

    return handleResponse<ApiResponse<T>>(response);
  }

  protected async upload<T>(endpoint: string, file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    const xhr = new XMLHttpRequest();
    
    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = (event.loaded / event.total) * 100;
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (error) {
            reject(new ApiError('Invalid JSON response', xhr.status));
          }
        } else {
          reject(new ApiError(`Upload failed: ${xhr.statusText}`, xhr.status));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new ApiError('Upload failed', 0));
      });

      xhr.open('POST', `${this.baseUrl}${endpoint}`);
      const token = localStorage.getItem('authToken');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.send(formData);
    });
  }
} 